import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const values = [
  { icon: '💡', title: 'Innovation', desc: 'We stay at the frontier — constantly exploring emerging AI, cloud, and DevOps patterns.' },
  { icon: '🤝', title: 'Partnership', desc: 'We work as an extension of your team, not just a vendor.' },
  { icon: '🎯', title: 'Precision', desc: 'Every decision is deliberate — from architecture to UX to deployment.' },
  { icon: '🌱', title: 'Growth', desc: 'We build systems that grow with you, avoiding technical debt from day one.' },
];

const team = [
  { name: 'Karteek Vemula', role: 'Founder & CEO', emoji: '👨‍💼', desc: 'Visionary leader driving VistaraKlyQs\'s strategy and client relationships.' },
  { name: 'AI Architecture Lead', role: 'Head of AI Engineering', emoji: '🧠', desc: 'Designs autonomous agent pipelines and Gen AI product roadmaps.' },
  { name: 'Cloud Solutions Lead', role: 'Principal Cloud Architect', emoji: '☁️', desc: 'Multi-cloud expert spanning AWS, Azure, and GCP infrastructure.' },
  { name: 'Full-Stack Lead', role: 'Head of Engineering', emoji: '💻', desc: 'Leads MERN, PHP, and Python development across all product lines.' },
];

const milestones = [
  { year: '2014', label: 'Founded in Hyderabad, India' },
  { year: '2016', label: 'First healthcare & insurance platform launched' },
  { year: '2019', label: 'Expanded to AWS & Azure cloud consulting' },
  { year: '2021', label: 'Launched GenAI & chatbot practice' },
  { year: '2023', label: 'Multi-agent AI frameworks & Kubernetes practice' },
  { year: '2025', label: '150+ projects, 80+ global clients' },
];

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-bg"></div>
        <div className="section-wrapper page-hero-inner">
          <span className="section-tag reveal">Who We Are</span>
          <h1 className="section-title reveal delay-100">
            Building Tomorrow's Tech, <span>Today</span>
          </h1>
          <p className="section-subtitle reveal delay-200">
            VistaraKlyQs Tech LLP is a product engineering and AI company dedicated to transforming
            ideas into scalable digital products that create real business impact.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mv-section">
        <div className="section-wrapper mv-grid">
          <div className="mv-card glass-card reveal-left">
            <div className="mv-icon">🎯</div>
            <h3>Our Mission</h3>
            <p>To empower businesses with intelligent, scalable technology solutions — from AI agents to cloud platforms — that drive measurable growth and competitive advantage.</p>
          </div>
          <div className="mv-card glass-card reveal">
            <div className="mv-icon">🔭</div>
            <h3>Our Vision</h3>
            <p>To be the most trusted technology partner for companies building the next generation of AI-first products across healthcare, insurance, and enterprise software.</p>
          </div>
          <div className="mv-card glass-card reveal-right">
            <div className="mv-icon">💎</div>
            <h3>Our Promise</h3>
            <p>Every engagement delivers production-ready, well-tested, scalable software — built by senior engineers who take pride in their craft and treat your product as their own.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">What Drives Us</span>
            <h2 className="section-title">Our Core <span>Values</span></h2>
          </div>
          <div className="values-grid">
            {values.map((v, i) => (
              <div key={i} className={`value-card glass-card reveal delay-${i * 150}`}>
                <div className="value-icon">{v.icon}</div>
                <h4 className="value-title">{v.title}</h4>
                <p className="value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">Our Journey</span>
            <h2 className="section-title">A Decade of <span>Innovation</span></h2>
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div key={i} className={`timeline-item reveal delay-${i * 100} ${i % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-dot"></div>
                <div className="timeline-card glass-card">
                  <span className="timeline-year">{m.year}</span>
                  <p className="timeline-label">{m.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="team-section">
        <div className="section-wrapper">
          <div className="section-header reveal">
            <span className="section-tag">Leadership</span>
            <h2 className="section-title">The Minds <span>Behind the Magic</span></h2>
          </div>
          <div className="team-grid">
            {team.map((t, i) => (
              <div key={i} className={`team-card glass-card reveal delay-${i * 100 + 100}`}>
                <div className="team-emoji">{t.emoji}</div>
                <h4 className="team-name">{t.name}</h4>
                <span className="team-role">{t.role}</span>
                <p className="team-desc">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta">
        <div className="section-wrapper services-cta-inner reveal">
          <h2 className="cta-title">Ready to <span>work with us?</span></h2>
          <p className="cta-sub">Join 80+ companies who trust VistaraKlyQs to power their digital transformation.</p>
          <Link to="/contact" className="btn-primary">Get in Touch</Link>
        </div>
      </section>
    </div>
  );
};

export default About;
